import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, Grow, Paper, Popper, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { isFunction } from "lodash";
import { SelfServiceDropdownProps, SelfServiceRenderPropsBase, SelfServiceRenderToggleArgs } from "@myProfile/models";

const useStyles = makeStyles<
  Theme,
  {
    width?: number;
    stretchToContentWidth?: boolean;
  }
>((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip,
    minWidth: ({ width }) => width,
    maxWidth: ({ stretchToContentWidth, width }) => (stretchToContentWidth ? "auto" : width),
  },
  arrow: {
    width: 10,
    height: 10,
    "&::before": {
      content: '""',
      display: "block",
      transform: "translateX(10px) rotate(45deg)",
    },
  },
}));

const SelfServiceDropdown: FunctionComponent<SelfServiceDropdownProps> = ({
  children,
  renderToggle,
  withArrow,
  width,
  placement,
  stretchToContentWidth,
  constrainToDialog,
}) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [paperMaxHeight, setPaperMaxHeight] = useState<number | undefined>(undefined);

  const classes = useStyles({
    width: width ?? anchorEl.current?.clientWidth,
    stretchToContentWidth,
  });

  const handleToggleDropdown = useCallback(() => setOpen(!open), [setOpen, open]);

  const handleOpenDropdown = useCallback(() => setOpen(true), [setOpen]);

  const handleCloseDropdown = useCallback(() => setOpen(false), [setOpen]);

  const renderProps = useMemo<SelfServiceRenderPropsBase>(
    () => ({
      toggleDropdown: handleToggleDropdown,
      openDropdown: handleOpenDropdown,
      closeDropdown: handleCloseDropdown,
    }),
    [handleOpenDropdown, handleCloseDropdown, handleToggleDropdown]
  );

  const renderToggleArgs = useMemo<SelfServiceRenderToggleArgs>(
    () => ({
      open,
      ref: anchorEl,
      ...renderProps,
    }),
    [open, renderProps]
  );

  const modifiers = useMemo(
    () => [
      {
        name: "arrow",
        enabled: withArrow,
      },
    ],
    [withArrow]
  );

  const CONSISTENT_MAX_HEIGHT = 280;

  useEffect(() => {
    if (!constrainToDialog) {
      setPaperMaxHeight(undefined);

      return;
    }

    if (open && anchorEl.current) {
      const updateMaxHeight = () => {
        const anchor = anchorEl.current;

        if (!anchor) return;

        const anchorRect = anchor.getBoundingClientRect();
        const dropdownTop = anchorRect.bottom;

        const dialogPaper = anchor.closest(".MuiDialog-paper");
        const padding = 30;

        let bottomBoundary: number;

        if (dialogPaper) {
          const dialogRect = dialogPaper.getBoundingClientRect();

          bottomBoundary = dialogRect.bottom - padding;
        } else {
          bottomBoundary = window.innerHeight - padding;
        }

        const availableHeight = bottomBoundary - dropdownTop;

        setPaperMaxHeight(Math.min(Math.max(availableHeight, 100), CONSISTENT_MAX_HEIGHT));
      };

      const timer = setTimeout(updateMaxHeight, 0);

      return () => clearTimeout(timer);
    }
  }, [open, constrainToDialog]);

  return (
    <ClickAwayListener onClickAway={handleCloseDropdown}>
      <Box>
        {renderToggle(renderToggleArgs)}
        <Popper
          transition
          placement={placement}
          open={open}
          anchorEl={anchorEl.current}
          className={classes.popper}
          modifiers={modifiers}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Box>
                {withArrow && <span data-popper-arrow={true} className={classes.arrow} />}
                <Paper
                  ref={paperRef}
                  elevation={5}
                  data-testid="dropdown-children"
                  style={{
                    maxHeight: paperMaxHeight ? `${paperMaxHeight}px` : undefined,
                    overflowY: paperMaxHeight ? "auto" : undefined,
                  }}
                >
                  {isFunction(children) ? children(renderProps) : children}
                </Paper>
              </Box>
            </Grow>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

SelfServiceDropdown.defaultProps = {
  withArrow: false,
  stretchToContentWidth: false,
  placement: "bottom",
  constrainToDialog: false,
};

export default SelfServiceDropdown;
